'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers'

const LineExplorer = dynamic(() => import('../_components/widgets/NumberWidgets').then(m => m.LineExplorer), { ssr: false })

export default function Lines() {
  return (
    <Lesson>
      <Step>
        <H>The most useful shape</H>
        <P>The simplest graph of all is a straight line, and every straight line follows the same rule:</P>
        <MB>{'\\amber{y} = \\green{m}\\,\\blue{x} + \\green{b}'}</MB>
        <P>
          Two numbers control it. <M>{'\\green{b}'}</M> is where the line crosses the up–down axis (the{' '}
          <strong>intercept</strong>). <M>{'\\green{m}'}</M> is the <strong>slope</strong>: how much the line rises for every one
          step to the right. They’re <Hl c="green">green</Hl> because they’re the line’s <em>parameters</em> — the knobs.
        </P>
        <Figure n="2.6" caption="Turn the two knobs. The little triangle shows the slope: one step right (run), m steps up (rise).">
          <LineExplorer />
        </Figure>
        <Callout kind="try">Make the slope negative. Set it to exactly 0. What does b do when m is 0?</Callout>
      </Step>

      <Step gate="goal-line">
        <H>Fit the line by hand</H>
        <P>
          Here’s a preview of what machine learning <em>does</em>: find the knob settings that make the line match some data.
          For now, the data is just two points, and you are the learning algorithm.
        </P>
        <Goal id="goal-line" task={<>Adjust m and b until the line passes through both circled points.</>}>
          {onReach => (
            <LineExplorer
              targets={[
                [-2, -1],
                [2, 3],
              ]}
              onGoal={onReach}
            />
          )}
        </Goal>
      </Step>

      <Step gate={['nq-slope', 'line-through']}>
        <H>Slope from two points</H>
        <P>You can calculate the slope instead of searching for it. Between any two points on a line:</P>
        <MB>{'\\green{m} = \\frac{\\text{rise}}{\\text{run}} = \\frac{y_2 - y_1}{x_2 - x_1}'}</MB>
        <Callout kind="notation">
          The small numbers below the letters are <strong>subscripts</strong>: just labels, not math. <M>{'x_1'}</M> means “the x
          of point 1”. Read <M>{'y_2 - y_1'}</M> as “how much y changed between the points”.
        </Callout>
        <NumberQuiz id="nq-slope" question={<>What is the slope of the line through <M>{'(1, 2)'}</M> and <M>{'(4, 11)'}</M>?</>} answer={3} explain={<>Rise 11 − 2 = 9, run 4 − 1 = 3, slope 9 ÷ 3 = 3.</>} hint={<>Rise is the change in y; run is the change in x. Divide rise by run.</>} />
        <Exercise
          id="line-through"
          title="Compute a line from two points"
          {...EX.lineThrough}
          hints={[
            <>
              <C>return (y2 - y1) / (x2 - x1)</C>
            </>,
            <>
              In lineThrough: <C>const m = slope(x1, y1, x2, y2)</C>, then <C>{'return { m, b: y1 - m * x1 }'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-model">
        <H>Your first model, hiding in plain sight</H>
        <P>
          Here’s the twist: <M>{'\\amber{y} = \\green{m}\\blue{x} + \\green{b}'}</M> <em>is</em> a machine-learning model. Give it
          an <Hl c="blue">input</Hl> (a house’s size), it makes a <Hl c="amber">prediction</Hl> (a price). Its{' '}
          <Hl c="green">parameters</Hl> m and b are what training adjusts.
        </P>
        <P>In machine learning we usually rename them: <M>{'\\green{w}'}</M> for “weight” instead of m. Same thing.</P>
        <MB>{'\\amber{\\hat{y}} = \\green{w}\\,\\blue{x} + \\green{b}'}</MB>
        <Callout kind="notation">
          The little hat on <M>{'\\hat{y}'}</M> (say “y-hat”) means “predicted y” — the model’s guess, as opposed to the true{' '}
          <M>y</M>.
        </Callout>
        <Exercise id="predict-line" title="A line as a predictor" {...EX.predictLine} />
        <Quiz
          id="q-model"
          question="In the model ŷ = w·x + b, which numbers does training change?"
          options={[
            { label: 'x and ŷ', why: 'x is the input (data you’re given) and ŷ is the output. Training doesn’t change the data.' },
            { label: 'w and b', correct: true },
            { label: 'Only ŷ', why: 'ŷ is computed from the others — change w and b and ŷ follows.' },
          ]}
          explain="The parameters w and b are the model’s ‘knowledge’. Learning = finding good values for them. A large language model is the same, with billions of w’s."
        />
      </Step>
    </Lesson>
  )
}
